<script lang="ts">
	import Svg from '#lib/svg/svg.svelte';
	import { WidgetButtonStyle } from '#lib/types/generated/index.js';
	import type { Icon } from '#lib/types/icons.js';
	import { widgetConfig } from '#lib/widget/core.js';
	import { classesDependingOnProfile, getRoundedClasses } from '#lib/widget/theme.js';
	import { goto } from '$app/navigation';
	import type { Snippet } from 'svelte';
	import { fade } from 'svelte/transition';

	interface BaseProps {
		className?: string;
		zoom?: boolean;
		transparentBackground?: boolean;
		textClasses?: string;
		useOutline?: boolean;
		height?: string;
		width?: string;
		padding?: string;
		disabled?: boolean;
		selected?: boolean;
		icon?: Icon;
		iconPosition?: 'left' | 'right';
		iconSizeClasses?: string;
		overrideCursor?: boolean;
		scaleOnHover?: boolean;
		backgroundImage?: string;
		rounded?: 'auto' | WidgetButtonStyle;
		children?: Snippet;
	}

	type Props = BaseProps &
		({ onclick: () => void; href?: never } | { href: string; onclick?: never });

	let {
		className = '',
		zoom = false,
		transparentBackground = false,
		textClasses = 'text-widget-primary-on dark:text-widget-primary-dark-on',
		useOutline = true,
		height = '',
		width = '',
		padding = 'p-2',
		href = '',
		disabled = false,
		selected = false,
		icon = '',
		iconPosition = 'left',
		iconSizeClasses = 'w-5 h-5',
		overrideCursor = false,
		scaleOnHover = false,
		backgroundImage = '',
		rounded = 'auto',
		onclick = undefined,
		children
	}: Props = $props();

	let element: HTMLDivElement | undefined = $state(undefined);

	let style = '';
	function click() {
		if (href) {
			if (/^(https?:)?\/\//i.test(href)) {
				window.location.href = href;
			} else {
				goto(href);
			}
		} else if (onclick) {
			onclick();
		} else {
			console.error('Missing action on this WidgetButton');
		}
	}

	function getBackgroundClasses(selected: boolean) {
		let classes = [];
		if (!transparentBackground) {
			classes.push('disabled:bg-widget-primary/20 disabled:dark:bg-widget-primary-dark/20 ');
			classes.push('bg-widget-primary dark:bg-widget-primary-dark');

			if (selected) {
				classes.push(
					classesDependingOnProfile(
						'brightness-125 contrast-100',
						'dark:brightness-125 dark:contrast-100'
					)
				);
			}
		}

		return classes.join(' ');
	}

	// outline-none hover:outline-widget-primary hover:dark:outline-widget-primary-dark disabled:outline-transparent disabled:dark:outline-transparent

	function getOutlineClasses(selected: boolean) {
		let classes = [];
		if (useOutline) {
			classes.push(
				'hover:outline-widget-primary hover:dark:outline-widget-primary-dark outline-offset-2 outline-2'
			);
			classes.push('disabled:outline-transparent dark:disabled:outline-transparent');

			if (selected) {
				classes.push('outline-widget-primary/60 dark:outline-widget-primary-dark/60');
			} else {
				classes.push('outline-transparent');
			}
		} else {
			classes.push('outline-none');
		}

		return classes.join(' ');
	}

	function getTextClasses() {
		let classes = ['disabled:opacity-50'];
		if (transparentBackground) {
			classes.push('hover:text-widget-primary dark:hover:text-widget-primary-dark');
		}

		if (backgroundImage) {
			classes = ['text-[#FFFFFF]', ...classes, 'text-lg font-medium'];
		} else {
			classes = [textClasses, ...classes];
		}

		return classes.join(' ');
	}

	// Do not apply rounded classes if there is a background image
	let buttonRoundedClasses = $derived(
		backgroundImage ? 'rounded-md' : getRoundedClasses($widgetConfig.theme.buttonStyle, rounded)
	);
</script>

<div bind:this={element} class="h-full flex {width}">
	<button
		in:fade={{ duration: 200 }}
		class="relative flex-none transition-all duration-300
        {buttonRoundedClasses}
        {getBackgroundClasses(selected)}
        {getOutlineClasses(selected)}
        {getTextClasses()}
        {scaleOnHover ? 'hover:scale-[1.02] hover:z-10' : ''}
        {padding}
        flex justify-center gap-2
        {backgroundImage ? 'items-end' : 'items-center'}
        flex-nowrap text-nowrap truncate
        {className}
        {height}
        {zoom ? 'zoom-animation' : ''}
        {overrideCursor ? 'cursor-default' : ''}
      "
		{disabled}
		class:cursor-not-allowed={disabled}
		type="button"
		{style}
		onclickcapture={(e) => {
			e.stopPropagation();
			click();
		}}
	>
		{#if backgroundImage}
			<div class="absolute top-0 inset-0 bg-black">
				<img src={backgroundImage} alt="" class="object-cover w-full h-full" />
			</div>
			<div
				class="absolute top-0 bg-linear-to-t from-[rgba(0,0,0,0.8)]/50 to-[rgba(0,0,0,0.1)]/50 w-full h-full"
			></div>
		{/if}
		<span
			class="relative flex items-center justify-center gap-2 text-wrap {iconPosition ===
			'right'
				? 'flex-row-reverse'
				: ''}"
		>
			{#if icon}
				<Svg src={icon} className="{iconSizeClasses} shrink-0" />
			{/if}

			{@render children?.()}
		</span>
	</button>
</div>

<style>
	@keyframes zoom {
		0% {
			transform: scale(0);
			opacity: 0;
		}
		25% {
			transform: scale(1.5);
			opacity: 1;
		}
		50% {
			transform: scale(1);
			opacity: 1;
		}
		75% {
			transform: scale(1.2);
			opacity: 1;
		}
		100% {
			transform: scale(1);
			opacity: 1;
		}
	}

	.zoom-animation {
		animation: zoom 0.4s linear 1;
	}
</style>
